import { FC, useEffect, useState, useRef } from 'react';
import { useSpring, animated } from '@react-spring/web';
import cx from 'classnames';
import styles from './Swatch.module.scss';
import A11ySwatches from '../A11ySwatches';
import { useFormikContext, Field } from 'formik';
import colorMethods from '../../../utils/color';
import { useColorRamp } from '../ColorRampContext';

export interface SwatchProps {
  colors: any[];
  color: any;
  title: string;
  id: string;
}

interface SwatchFormProps {
  formColor: string;
  color: any;
}

const SwatchForm: FC<SwatchFormProps> = ({ formColor, color }) => {
  const { values, setFieldValue } = useFormikContext();
  const [isLoaded, setIsLoaded] = useState(false);

  // @ts-ignore

  // @ts-ignore
  // const hex = values[rootName][rampIdx].colors[colorIdx].hex;

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <>
      <div className='formControl'>
        <label
          className={cx('formControl__label', {
            'formControl__label--disabled': true,
            'formControl__label--dark': formColor === '#000000',
          })}
        >
          Hex
        </label>
        <Field className='formControl__input' type='text' disabled={true} name='hex' value={color.hex} readOnly />
      </div>
      <div className='formControl'>
        <label
          className={cx('formControl__label', {
            'formControl__label--disabled': true,
            'formControl__label--dark': formColor === '#000000',
          })}
        >
          HSL
        </label>
        <div className='formControl__flex'>
          <div className='formControl__flexItem'>
            <Field
              className='formControl__input'
              type='number'
              value={color.hsl[0]}
              readOnly
              disabled={true}
              name={`hsl[0]`}
            />
            <span className='formControl__sublabel'>Hue</span>
          </div>
          <div className='formControl__flexItem'>
            <Field
              className='formControl__input'
              type='number'
              value={color.hsl[1]}
              readOnly
              disabled={true}
              name={`hsl[1]`}
            />
            <span className='formControl__sublabel'>Sat.</span>
          </div>
          <div className='formControl__flexItem'>
            <Field
              className='formControl__input'
              type='number'
              value={color.hsl[2]}
              readOnly
              disabled={true}
              name={`hsl[2]`}
            />
            <span className='formControl__sublabel'>Light.</span>
          </div>
        </div>
      </div>
      <div className='formControl'>
        <label
          className={cx('formControl__label', {
            'formControl__label--disabled': true,
            'formControl__label--dark': formColor === '#000000',
          })}
        >
          RGB
        </label>
        <div className='formControl__flex'>
          <div className='formControl__flexItem'>
            <Field
              disabled={true}
              className='formControl__input'
              type='number'
              name={`rgb[0]`}
              value={color.rgb[0]}
              readOnly
            />
            <span className='formControl__sublabel'>Red</span>
          </div>
          <div className='formControl__flexItem'>
            <Field
              disabled={true}
              className='formControl__input'
              type='number'
              name={`rgb[1]`}
              value={color.rgb[1]}
              readOnly
            />
            <span className='formControl__sublabel'>Green</span>
          </div>
          <div className='formControl__flexItem'>
            <Field
              disabled={true}
              className='formControl__input'
              type='number'
              name={`rgb[2]`}
              value={color.rgb[2]}
              readOnly
            />
            <span className='formControl__sublabel'>Blue</span>
          </div>
        </div>
      </div>
    </>
  );
};

export const Swatch: FC<SwatchProps> = ({ title, id, colors, color }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [formColor, setFormColor] = useState('#000000');

  const { showContrast, rampId } = useColorRamp();

  function toggleOpen() {
    setIsOpen(!isOpen);
  }

  const colorsRef = useRef<any[]>([]);

  useEffect(() => {
    // @ts-ignore
    colorsRef.current = [].concat(colors);
  }, [colors]);

  useEffect(() => {
    setIsLoaded(true);
    if (colors && colorMethods.contrast(color.rgb, [255, 255, 255], 4.5)) {
      setFormColor('#ffffff');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { swatchWidth } = useSpring({
    swatchWidth: isOpen ? '240px' : '40px',
  });

  return (
    <div>
      <h4 className={styles.name}>{title}</h4>
      <div className={cx(styles.swatch, { [styles['swatch--light']]: formColor === '#ffffff' })}>
        {isLoaded && (
          <>
            <div className={styles.swatch__clickZone} onClick={toggleOpen} />

            <animated.div
              className={styles.swatch__colorZone}
              // @ts-ignore
              style={{
                width: swatchWidth,
                background: `hsl(${color.hsl[0]}, ${color.hsl[1]}%, ${color.hsl[2]}%)`,
              }}
            >
              <animated.div
                className={styles.form}
                style={{
                  transform: isOpen ? `translate3d(0px, 0%, 0)` : `translate3d(-20px, 0, 0)`,
                  opacity: isOpen ? 1 : 0,
                }}
              >
                <div className={styles.form__inner}>
                  <SwatchForm color={color} formColor={formColor} />
                </div>
              </animated.div>
            </animated.div>
            {showContrast && showContrast === `showUiContrast-${rampId}` && (
              <div
                style={{
                  position: 'absolute',
                  top: '30px',
                  right: 0,
                  zIndex: 10,
                  borderRadius: '4px',
                  background: `hsl(${color.hsl[0]}, ${color.hsl[1]}%, ${color.hsl[2]}%)`,
                }}
              >
                <A11ySwatches id={id} type='ui' colors={colorsRef?.current} color={color} />
              </div>
            )}
            {showContrast && showContrast === `showTextContrast-${rampId}` && (
              <div
                style={{
                  position: 'absolute',
                  top: '30px',
                  right: 0,
                  zIndex: 10,
                  borderRadius: '4px',
                  background: `hsl(${color.hsl[0]}, ${color.hsl[1]}%, ${color.hsl[2]}%)`,
                }}
              >
                <A11ySwatches id={id} type='text' colors={colorsRef?.current} color={color} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default Swatch;
